import React, { useState, useRef, useEffect } from "react";
import classNames from "classnames";
import CarouselButtons from "../../../Pages/Ecommerce/Shared/CarouselButtons/CarouselButtons";

interface IExhibitionCarouselProps {
    displayData: { key: string; data: React.ReactNode }[];
    previousSlideAriaLabel: string;
    nextSlideAriaLabel: string;
    arrowColor?: string;
    customArrowColor?: string;
}

export const ExhibitionCarousel: React.FC<IExhibitionCarouselProps> = ({
    displayData,
    previousSlideAriaLabel,
    nextSlideAriaLabel,
    arrowColor = "white",
    customArrowColor,
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const isDragging = useRef(false);
    const startX = useRef(0);
    const translateX = useRef(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const slideCount = displayData.length;

    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 768);
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    const isAtStart = currentIndex === 0;
    const isAtEnd = currentIndex >= slideCount - 2;

    const handleNext = () => {
        if (isMobile) {
            setCurrentIndex((prev) => (prev + 1) % slideCount);
        } else {
            if (!isAtEnd) {
                setCurrentIndex((prev) => Math.min(prev + 1, slideCount - 2));
            }
        }
    };

    const handlePrev = () => {
        if (isMobile) {
            setCurrentIndex((prev) => (prev - 1 + slideCount) % slideCount);
        } else {
            if (!isAtStart) {
                setCurrentIndex((prev) => Math.max(prev - 1, 0));
            }
        }
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        isDragging.current = true;
        startX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = () => {
        if (!isDragging.current) return;

        if (translateX.current > 50) {
            handlePrev();
        } else if (translateX.current < -50) {
            handleNext();
        }

        isDragging.current = false;
        translateX.current = 0;
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        isDragging.current = true;
        startX.current = e.clientX;
    };

    useEffect(() => {
        const el = containerRef.current;

        const handleTouchMove = (e: TouchEvent) => {
            if (!isDragging.current) return;

            const deltaX = e.touches[0].clientX - startX.current;
            if (Math.abs(deltaX) > 10) {
                e.preventDefault();
            }

            translateX.current = deltaX;
        };

        if (el) {
            el.addEventListener("touchmove", handleTouchMove, { passive: false });
        }

        return () => {
            if (el) {
                el.removeEventListener("touchmove", handleTouchMove);
            }
        };
    }, []);

    const getSlideClass = (index: number) => {
        const normalized = (i: number) => (i + slideCount) % slideCount;

        const bigIndex = currentIndex;
        const smallIndex = normalized(currentIndex + 1);
        const hiddenLeftIndex = normalized(currentIndex - 1);
        const hiddenRightIndex = normalized(currentIndex + 2);

        const thisIndex = normalized(index);

        if (thisIndex === bigIndex) return "big-image";
        if (thisIndex === smallIndex) return "small-image";
        if (thisIndex === hiddenLeftIndex) return "hidden-left";
        if (thisIndex === hiddenRightIndex) return "hidden-right";

        return "hidden";
    };

    return (
        <div
            className="exhibition-carousel"
            ref={containerRef}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
        >
            <CarouselButtons
                hideLeftButton={!isMobile && isAtStart}
                hideRightButton={!isMobile && isAtEnd}
                isOrangeButtons={false}
                arrowColor={arrowColor}
                previousSlideAriaLabel={previousSlideAriaLabel}
                nextSlideAriaLabel={nextSlideAriaLabel}
                onLeftClick={handlePrev}
                onRightClick={handleNext}
                customArrowColor={customArrowColor}
            />

            <div className="exhibition-carousel__track">
                {displayData.map(({ key, data }, index) => (
                    <div
                        className={classNames("exhibition-carousel__slide", getSlideClass(index))}
                        key={key + "exhibition"}
                    >
                        {data}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ExhibitionCarousel;
